// Appliances that should render a second 3D model + cutout on top of the
// countertop (e.g., kegerator + tap). Keyed by the BOTTOM item's SKU.
// Adding a new pair is a one-line change here — no catalog/API/DB work.

export type TopModelExtension = {
	sku: string;
	cutoutDiameter: number;
	modelYOffset?: number;
	xOffset?: number;
	zOffset?: number;
};

export const TOP_MODEL_EXTENSIONS: Record<string, TopModelExtension> = {
	'C124KEG': { sku: 'C124KEG-TAP', cutoutDiameter: 2.25 }
};

export const resolveTopModel = (item: { sku?: string } | null | undefined): TopModelExtension | null => {
	if (!item?.sku) return null;
	return TOP_MODEL_EXTENSIONS[item.sku] ?? null;
};

// SKUs of the top models themselves (e.g. the tap), keyed off the extension's
// `sku`. Used to treat them as auto-added fixtures: hidden from estimate line
// items, and rendered as a top SVG layer so their wording sits above cutouts.
const TOP_MODEL_SKUS = new Set(Object.values(TOP_MODEL_EXTENSIONS).map((ext) => ext.sku));

export const isTopModelSku = (sku: string | null | undefined): boolean => !!sku && TOP_MODEL_SKUS.has(sku);

// Geometric CENTER of the tap, relative to the bottom item's center, in
// panel-local inches. Use this for anything positioned by its center:
//   - 2D SVG mask hole and visible cutout circle
//   - 3D countertop CSG cutout (cylinder)
//   - any future top-model surface fixture positioned by its midpoint
// xOffset/zOffset (from the catalog config) shift this center for tuning.
export const computeTopModelCenter = (
	topModel: TopModelExtension
): { x: number; z: number; radius: number } => ({
	x: topModel.xOffset ?? 0,
	z: topModel.zOffset ?? 0,
	radius: topModel.cutoutDiameter / 2
});

// PIVOT offset of the tap .dae from the bottom item's center, in panel-local
// inches. DAE files in this codebase are authored with their pivot at the
// LEFT-FRONT corner of an empty bounding box that matches the bottom item's
// footprint; the visible geometry sits centered inside that box. Placing the
// pivot at the bottom item's LEFT-FRONT corner makes the centered geometry
// land over the bottom item's center.
// Used ONLY for placing the 3D model itself — not for cutouts or 2D circles.
export const computeTopModelPivot = (
	bottomItemSize: { width?: number; depth?: number } | null | undefined,
	bottomItemModelOffsetX: number | null | undefined,
	topModel: TopModelExtension
): { x: number; z: number } => ({
	x: -((bottomItemSize?.width ?? 0) / 2) - (bottomItemModelOffsetX ?? 0) + (topModel.xOffset ?? 0),
	z: ((bottomItemSize?.depth ?? 0) / 2) + (topModel.zOffset ?? 0)
});
